import {ConnectorStatusEnum} from 'src/enums/chargers';
import {LanguageEnum} from 'src/enums/common';

// Guest Session Status enum
export enum GuestSessionStatus {
    PENDING = 'PENDING',
    AUTHORIZED = 'AUTHORIZED',
    STARTING = 'STARTING',
    CHARGING = 'CHARGING',
    STOPPING = 'STOPPING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
}

// Response from GET /api/public/guest/connector/{uuid}
export interface GuestConnectorDTO {
    uuid: string;
    connectorId: number;
    status: ConnectorStatusEnum;

    // ChargePoint info
    chargePointId: string;
    chargePointModel: string;

    // Location info
    locationName: string;
    locationAddress: string;

    // Tariff info (in 1/10000 units)
    pricePerKwh: number; // in 1/10000 units (4500 = 0.45 CHF)
    currency: string; // ISO 4217 (CHF, EUR, USD)
    holdAmount: number; // in 1/10000 units (500000 = 50.00 CHF)
}

// Response from POST /api/public/guest/session
export interface GuestSessionDTO {
    uuid: string;
    status: GuestSessionStatus;
    clientSecret: string; // Stripe Payment Intent client secret
    publishableKey: string; // Stripe publishable key
    pricePerKwh: number;
    currency: string;
    holdAmount: number;
}

// Response from GET /api/public/guest/session/{uuid}
export interface GuestSessionFullDTO {
    uuid: string;
    status: GuestSessionStatus;
    connector: GuestConnectorDTO;
    pricePerKwh: number;
    currency: string;
    holdAmount: number;
    transactionId?: number;
    wattsConsumed?: number; // in watts (5230 = 5.23 kWh)
    chargingTimeMinutes?: number;
    startedAt?: string; // ISO datetime
    stoppedAt?: string; // ISO datetime
    finalAmount?: number; // in 1/10000 units, only when COMPLETED

    // Where a receipt was emailed, if the guest asked for one. One per session, so a non-null
    // receiptSentAt is also what tells the page it has nothing left to offer.
    receiptEmail?: string;
    receiptSentAt?: string; // ISO datetime
}

// Request for POST /api/public/guest/session
export interface CreateGuestSessionRequest {
    connectorUuid: string;
}

// Request for POST /api/public/guest/session/{uuid}/receipt
export interface SendGuestReceiptRequest {
    uuid: string;
    email: string;
    language?: LanguageEnum;
}

// WebSocket message types

// Connector status update from /topic/guest-connector/{connectorUuid}
export interface GuestConnectorStatusUpdate {
    connectorUuid: string;
    status: ConnectorStatusEnum;
}

// Session status update from /topic/guest-session/{sessionUuid}
export interface GuestSessionStatusUpdate {
    status: GuestSessionStatus;
    message?: string;
    transactionId?: number;
}

export interface GuestSessionChargingUpdate {
    status: GuestSessionStatus.CHARGING;
    wattsConsumed: number;
    chargingTimeMinutes: number;
}

export interface GuestSessionCompletedUpdate {
    status: GuestSessionStatus.COMPLETED;
    finalAmount: number;
    wattsConsumed: number;
}

export interface GuestSessionErrorUpdate {
    status: GuestSessionStatus.FAILED;
    message: string;
}

export type GuestWebSocketMessage =
    | GuestSessionStatusUpdate
    | GuestSessionChargingUpdate
    | GuestSessionCompletedUpdate
    | GuestSessionErrorUpdate;

// Page state for Guest Charging flow
export type GuestPageState =
    | 'loading'
    | 'connector_info'
    | 'payment'
    | 'processing'
    | 'starting_charge'
    | 'charging'
    | 'stopping_charge'
    | 'completed'
    | 'error'
    | 'cancelled'
    | 'not_found';
